import React, { useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import './../components/landing/landing.css';
import { AMBER, YELLOW, GREEN, BLUE, NAVY, IMG, Reveal, Overline, CountUp, Marquee, Nav, Footer } from '../components/landing/shared';
import { Pillars, ProposalShowcase, Process, Incentives, Reviews, FinalCta } from '../components/landing/Sections';
import AiScanShowcase from '../components/landing/AiScanShowcase';
import SavingsVisualizer from '../components/landing/SavingsVisualizer';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const HERO_STATS = [
  { v: 20, suffix: '+', label: 'Years EPC experience' },
  { v: 1200, suffix: '+', label: 'Northeast homes powered' },
  { v: 25, suffix: ' yr', label: 'Warranty coverage' },
  { v: 100, suffix: '%', label: 'Designed on your real roof' },
];

const TRUST = [
  ['fa-shield-halved', '25-Year Warranty'],
  ['fa-house-chimney-crack', 'Roof-Leak Guarantee'],
  ['fa-bolt', 'Production Guarantee'],
  ['fa-hand-holding-dollar', '30% Federal Tax Credit'],
  ['fa-star', '4.9 / 5 Homeowner Rating'],
];

function QuoteForm() {
  const [form, setForm] = useState({ name: '', address: '', email: '', phone: '', monthly_bill: '' });
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try { await axios.post(`${API_URL}/api/public/quote-request`, form); setSent(true); }
    catch { toast.error('Could not send — please call us.'); }
    setBusy(false);
  };
  return (
    <div className="relative">
      <div className="form-glow" />
      <form id="quote" onSubmit={submit} className="relative rounded-[28px] p-6 sm:p-8 glass-card text-slate-900 shadow-2xl" data-testid="quote-form">
        {sent ? (
          <div className="text-center py-10" data-testid="quote-sent"><div className="w-16 h-16 mx-auto rounded-full grid place-items-center text-2xl text-white mb-4" style={{ background: GREEN }}><i className="fa-solid fa-check" /></div><h3 className="font-heading font-black text-2xl">Your roof is in the queue.</h3><p className="text-slate-500 text-sm mt-2">Samaritan Vision is pulling your satellite imagery now. A Samaritan advisor will send your 13-page proposal — built on your actual roof — within one business day.</p></div>
        ) : (
          <>
            <div className="flex items-center justify-between"><Overline color={GREEN}>FREE · NO OBLIGATION</Overline><span className="text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full" style={{ background: '#FFF6DC', color: '#8A5A00' }}>~60 SEC</span></div>
            <h3 className="font-heading font-black text-2xl sm:text-3xl mt-2 leading-tight">See your roof, designed by AI.</h3>
            <p className="text-slate-500 text-sm mt-1">Enter your address and we&apos;ll model your system, your savings and your incentives.</p>
            <div className="grid sm:grid-cols-2 gap-3 mt-5">
              {[['name', 'Full name', 'text', 'fa-user'], ['phone', 'Mobile', 'tel', 'fa-mobile-screen'], ['address', 'Home address', 'text', 'fa-location-dot'], ['email', 'Email', 'email', 'fa-envelope'], ['monthly_bill', 'Average monthly electric bill ($)', 'number', 'fa-bolt']].map(([k, ph, t, ic]) => (
                <label key={k} className={`relative block ${k === 'address' || k === 'monthly_bill' ? 'sm:col-span-2' : ''}`}>
                  <i className={`fa-solid ${ic} absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xs`} />
                  <input type={t} required={k !== 'monthly_bill'} placeholder={ph} value={form[k]} onChange={e => setForm({ ...form, [k]: e.target.value })} className="w-full h-12 pl-10 pr-4 rounded-xl border border-slate-200 bg-white text-sm focus:outline-none focus:border-[#21A84A] focus:ring-4 focus:ring-[#21A84A]/10 transition-shadow" data-testid={`quote-${k}`} />
                </label>
              ))}
            </div>
            <button type="submit" disabled={busy} className="btn-amber w-full mt-5 py-4 text-base disabled:opacity-60" data-testid="quote-submit">{busy ? <i className="fa-solid fa-spinner fa-spin" /> : <>Design my roof &amp; show my savings <i className="fa-solid fa-arrow-right text-sm" /></>}</button>
            <div className="flex items-center justify-center gap-5 text-[11px] text-slate-400 mt-4"><span><i className="fa-solid fa-lock mr-1" />Never sold</span><span><i className="fa-solid fa-phone-slash mr-1" />No robocalls</span><span><i className="fa-solid fa-satellite mr-1" />Real satellite data</span></div>
          </>
        )}
      </form>
    </div>
  );
}

function Hero() {
  return (
    <header className="hero-stage landing-grain" data-testid="landing-hero">
      <img src={IMG.heroHome} alt="Home with a full rooftop solar array on a bright sunny day" className="hero-photo" data-testid="hero-photo" />
      <div className="hero-veil" />
      <div className="hero-veil-b" />
      <div className="hero-sunbeam" />
      <div className="hero-rays" />

      <div className="relative w-full max-w-7xl mx-auto px-5 sm:px-8 pt-32 pb-8 lg:pt-36">
        <div className="max-w-3xl">
          <div className="hero-badge-pop inline-flex items-center gap-2 px-3.5 py-2 rounded-full border border-white/25 backdrop-blur-md text-[10px] font-bold tracking-[0.28em]" style={{ color: YELLOW, background: 'rgba(8,26,16,0.45)', animationDelay: '120ms' }} data-testid="hero-badge">
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: YELLOW }} />
            NJ · NY · CT · PA · AI-DESIGNED SOLAR
          </div>

          <h1 className="font-heading font-black text-white mt-6 tracking-tight leading-[0.92] text-[clamp(2.6rem,7.4vw,5.6rem)]" style={{ textShadow: '0 6px 34px rgba(4,18,10,0.55)' }} data-testid="hero-title">
            <span className="rise block" style={{ animationDelay: '220ms' }}>Real homes.</span>
            <span className="rise block" style={{ animationDelay: '380ms' }}>Real sunlight.</span>
            <span className="rise block hl-gold" style={{ animationDelay: '540ms' }}>Real savings.</span>
          </h1>

          <div className="hero-hr mt-7 rise" style={{ animationDelay: '700ms' }} />

          <p className="rise text-white/85 mt-6 text-lg sm:text-xl max-w-2xl leading-relaxed" style={{ animationDelay: '780ms' }}>
            This is a <span className="text-white font-semibold">real Samaritan install</span> — not a rendering. We design your array on your actual roof with AI satellite vision, show you <span style={{ color: YELLOW }} className="font-semibold">every dollar for 25 years</span>, and back it with guarantees nobody else in the Northeast offers.
          </p>

          <div className="rise flex flex-wrap gap-3 mt-9" style={{ animationDelay: '900ms' }}>
            <a href="#quote" className="btn-amber px-8 py-4 text-base" data-testid="hero-cta-btn">See my savings <i className="fa-solid fa-arrow-right text-sm" /></a>
            <a href="#vision" className="inline-flex items-center gap-2 font-semibold px-7 py-4 rounded-full border border-white/30 text-white backdrop-blur-md hover:bg-white/15 transition-colors" data-testid="hero-secondary-btn"><i className="fa-solid fa-play text-xs" style={{ color: YELLOW }} />Watch the AI design a roof</a>
          </div>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mt-14 lg:mt-20" data-testid="hero-stats">
          {HERO_STATS.map((s, i) => (
            <div key={s.label} className="stat-glass rise p-5" style={{ animationDelay: `${1050 + i * 130}ms` }} data-testid={`hero-stat-${i}`}>
              <p className="font-heading font-black text-white text-3xl sm:text-4xl tabular-nums"><CountUp to={s.v} />{s.suffix}</p>
              <p className="text-[9px] sm:text-[10px] font-bold tracking-[0.18em] mt-1.5 uppercase" style={{ color: YELLOW }}>{s.label}</p>
            </div>
          ))}
        </div>

        <div className="flex justify-center mt-10">
          <a href="#trust" className="scroll-cue text-white/70 text-xl" aria-label="Scroll down" data-testid="hero-scroll-cue"><i className="fa-solid fa-chevron-down" /></a>
        </div>
      </div>
    </header>
  );
}

export default function LandingPage() {
  return (
    <div id="top" className="landing min-h-screen text-white" style={{ background: NAVY }} data-testid="landing-page">
      <Nav />
      <Hero />

      <div id="trust" className="trust-strip relative z-10" data-testid="trust-strip">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {TRUST.map(([ic, t], i) => (
            <Reveal key={t} delay={i * 80} as="span" className="inline-flex items-center gap-2.5 text-xs sm:text-sm font-bold tracking-wide text-white/85">
              <i className={`fa-solid ${ic}`} style={{ color: i % 2 ? GREEN : YELLOW }} />{t}
            </Reveal>
          ))}
        </div>
      </div>

      <section className="relative px-5 sm:px-8 py-20 lg:py-28 overflow-hidden" style={{ background: NAVY }}>
        <div className="orb w-[520px] h-[520px] -top-40 right-[-140px]" style={{ background: AMBER, opacity: .16 }} />
        <div className="orb w-[420px] h-[420px] bottom-0 left-[-160px]" style={{ background: BLUE, opacity: .2, animationDelay: '-6s' }} />
        <div className="absolute inset-0 landing-grid" />
        <div className="relative max-w-7xl mx-auto grid lg:grid-cols-[1fr_1.1fr] gap-10 lg:gap-14 items-center">
          <Reveal>
            <Overline>WHAT YOU GET · FREE</Overline>
            <h2 className="font-heading font-black text-3xl sm:text-4xl mt-3 tracking-tight leading-[1.05]">Not a quote. A complete engineering study of your home.</h2>
            <ul className="mt-6 space-y-3">
              {[['fa-satellite-dish', 'AI-designed panel layout on your real satellite roof'], ['fa-file-invoice-dollar', '13-page proposal: cash vs loan vs lease, 25 years out'], ['fa-cloud-sun', 'Weather-adjusted production for your exact ZIP'], ['fa-hand-holding-dollar', 'Every federal + state incentive you qualify for, filed by us']].map(([ic, t]) => (
                <li key={t} className="flex items-start gap-3 text-slate-200"><span className="w-8 h-8 rounded-lg grid place-items-center shrink-0" style={{ background: 'rgba(250,171,28,0.14)' }}><i className={`fa-solid ${ic} text-sm`} style={{ color: AMBER }} /></span><span className="pt-1">{t}</span></li>
              ))}
            </ul>
            <div className="mt-8 flex items-center gap-4"><div className="flex -space-x-2">{[IMG.couple, IMG.family, IMG.installer].map((s, i) => <img key={i} src={s} alt="" className="w-9 h-9 rounded-full object-cover border-2" style={{ borderColor: NAVY }} />)}</div><p className="text-sm text-slate-400"><span className="text-white font-bold"><CountUp to={1200} suffix="+" /></span> Northeast homes powered</p></div>
          </Reveal>
          <Reveal delay={120}><QuoteForm /></Reveal>
        </div>
      </section>

      <Marquee />

      <section id="vision" className="relative px-5 sm:px-8 py-24 lg:py-32 overflow-hidden" style={{ background: NAVY }}>
        <div className="orb w-[500px] h-[500px] top-10 left-1/2" style={{ background: BLUE, opacity: .15 }} />
        <div className="relative max-w-7xl mx-auto"><Reveal><AiScanShowcase /></Reveal></div>
      </section>

      <section id="savings" className="bg-[#F7FAF8] text-slate-900 px-5 sm:px-8 py-24 lg:py-32">
        <div className="max-w-7xl mx-auto"><Reveal><SavingsVisualizer /></Reveal></div>
      </section>

      <Pillars />
      <ProposalShowcase />
      <Process />
      <Incentives />
      <Reviews />
      <FinalCta />
      <Footer />

      <div className="fixed bottom-0 inset-x-0 z-40 p-3 sm:hidden" style={{ background: 'linear-gradient(180deg, transparent, rgba(12,36,23,0.95) 40%)' }} data-testid="mobile-sticky-cta">
        <a href="#quote" className="btn-amber w-full py-3.5 text-sm">Design my roof — free <i className="fa-solid fa-arrow-right text-xs" /></a>
      </div>
    </div>
  );
}
